import Image from "next/image";
import Skeleton from "react-loading-skeleton";
import InitialCard from "../../../common/InitialCard";
import Locations from "./cart/locations";
import UserLoginSwitch from "./user-login-switch";
import styles from "./index.module.css";
import {
  searchIcon,
  cartIcon,
  searchWhiteIcon,
  closeIcon,
  marketPlacelogo,
} from "../../../../assets";

const getStoreFrontLabel = ({ organisation, accessPoints, accessPointId }) => {
  const fallback = organisation?.settings?.storeName || "StoreFront";
  const safePoints = accessPoints || [];

  if (safePoints.length <= 1) return fallback;

  const point = safePoints.find((p) => p.accessPointId === accessPointId);
  if (point?.name === "Main Entrance") return fallback;

  return `Location: ${point?.name || "Unknown Location"}`;
};

const shouldUseMarketplaceSearch = ({
  navLocation,
  storePath,
  onSeeAllSearchResults,
}) =>
  navLocation === "marketplace" &&
  storePath !== "market-place" &&
  Boolean(onSeeAllSearchResults);

const MarketplaceLogo = ({ onClick }) => (
  <div className={styles.brandMarketImagecontainer} onClick={onClick}>
    <Image
      src={marketPlacelogo}
      alt="circleplug brand logo"
      className={styles.brandlogo}
      quality={1}
    />
  </div>
);

const StoreIdentitySkeleton = () => (
  <>
    <div
      style={{
        width: "5rem",
        height: "5rem",
        borderRadius: "50%",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: "0.5rem",
      }}
    >
      <Skeleton circle height={"5rem"} width={"5rem"} />
    </div>
    <div className={styles.handleCard}>
      <div className={styles.handleName}>
        <Skeleton width={150} height={20} />
      </div>
      <div className={styles.storeFront}>
        <Skeleton width={100} height={16} />
      </div>
    </div>
  </>
);

const StoreIdentity = ({
  organisation,
  accessPoints,
  accessPointId,
  navLocation,
  urlPath,
}) => {

  if (navLocation === "marketplace" && urlPath !== "home") {
    return <MarketplaceLogo />;
  }

  return (
    <>
      <InitialCard
        image={organisation?.logoUrl ?? ""}
        name={organisation?.orgName ?? ""}
      />
      <div className={styles.handleCard}>
        <div className={styles.handleName}>{organisation?.orgName ?? ""}</div>
        <div className={styles.storeFront}>
          {getStoreFrontLabel({ organisation, accessPoints, accessPointId })}
        </div>
      </div>
    </>
  );
};

const HeaderSearchForm = ({
  searchTerm,
  searchInputRef,
  handleSearch,
  hasTyped,
  cancelSearch,
  searchFocus,
  setSearchFocus,
  urlWithSearchQuery,
  navLocation,
  storePath,
  onSeeAllSearchResults,
}) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;

    if (
      shouldUseMarketplaceSearch({
        navLocation,
        storePath,
        onSeeAllSearchResults,
      })
    ) {
      onSeeAllSearchResults();
    } else {
      urlWithSearchQuery(searchTerm);
    }

    if (searchFocus) setSearchFocus(false);
  };

  return (
    <form className={styles.searchbar} onSubmit={handleSubmit}>
      <input
        placeholder="Search a product..."
        ref={searchInputRef}
        value={searchTerm}
        onChange={(e) => handleSearch(e)}
      />

      {hasTyped ? (
        <span
          type="button"
          className={styles.cancelSearch}
          onClick={(e) => {
            cancelSearch(e);
          }}
        >
          {closeIcon}
        </span>
      ) : (
        <button
          type="submit"
          className={`${styles.searchButton} ${styles.ovalButton}`}
        >
          {searchWhiteIcon} Search
        </button>
      )}
    </form>
  );
};

const MarketplaceSearchPrompt = ({
  searchTerm,
  storePath,
  navLocation,
  onSeeAllSearchResults,
}) => {
  if (
    searchTerm.trim() === "" ||
    storePath === "market-place" ||
    navLocation !== "marketplace"
  ) {
    return null;
  }

  return (
    <div
      className={styles.searchPrompt}
      style={{
        paddingTop: ".5rem",
        paddingLeft: "1rem",
        color: "#0e86d4",
        fontSize: "1.2rem",
        cursor: "pointer",
      }}
      onClick={onSeeAllSearchResults}
    >
      <p>See all search results from all our stores?..</p>
    </div>
  );
};

const HeaderActions = ({
  modal,
  showSearchBar,
  accessPoints,
  accessPointId,
  storeBoxLocationChange,
  storePath,
  isSticky,
  setSearchFocus,
  setHasTyped,
  isPageMode,
  showCart,
  cartCount,
  onLogout,
  handleLoginClick,
  organisation,
}) => (
  <>
    {(modal === true && showSearchBar) || !showSearchBar ? (
      <Locations
        locations={accessPoints}
        accessPointId={accessPointId}
        handleLocationChange={storeBoxLocationChange}
        storePath={storePath}
        dropdownThemeKey="storeHeaderLocation"
      />
    ) : null}

    {(showSearchBar || isSticky) && !modal && (
      <div
        className={styles.searchIconOnly}
        onClick={() => {
          setSearchFocus(true);
          setHasTyped?.(true);
        }}
      >
        {searchIcon}
      </div>
    )}

    <div
      className={styles.topCart}
      onClick={() => {
        if (!isPageMode) showCart();
      }}
    >
      <div className={styles.topCartIcon}>
        <span>{cartIcon}</span>
        {cartCount > 0 && <sup className={styles.cartCount}>{cartCount}</sup>}
      </div>
      <div className={styles.topCartText}>Cart</div>
    </div>

    <div className="header-login">
      <UserLoginSwitch
        onLogout={onLogout}
        handleLoginClick={handleLoginClick}
        dropdownMode={true}
        storePath={storePath}
        organisation={organisation}
        accessPointId={accessPointId}
      />
    </div>
  </>
);

const StoreHeader = ({
  headerRef,
  headerClassName,
  showSearchBar,
  isSticky,
  isAtTop,
  searchFocus,
  storePath,
  urlPath,
  hasSearch,
  handleLogoClick,
  isSwitching,
  organisation,
  accessPoints,
  accessPointId,
  hasTyped,
  isHomePage,
  pastHomeSearch,
  searchTerm,
  searchInputRef,
  handleSearch,
  cancelSearch,
  setSearchFocus,
  setHasTyped,
  urlWithSearchQuery,
  navLocation,
  onSeeAllSearchResults,
  modal,
  storeBoxLocationChange,
  isPageMode,
  showCart,
  cartCount,
  onLogout,
  handleLoginClick,
}) => {
  return (
    <header
      ref={headerRef}
      className={`${headerClassName} ${
        showSearchBar || isSticky ? styles.stickyHeader : ""
      }`}
      style={
        showSearchBar || isSticky ? { top: isAtTop ? "" : "0" } : { top: "" }
      }
    >
      <div className={styles.headerContent}>
        {!searchFocus && storePath !== "market-place" && (
          <div
            className={`${styles.topimageinitials} ${
              hasSearch ? styles.searchingActive : ""
            }`}
            onClick={handleLogoClick}
          >
            {isSwitching ? (
              <StoreIdentitySkeleton />
            ) : (
              <StoreIdentity
                organisation={organisation}
                accessPoints={accessPoints}
                accessPointId={accessPointId}
                navLocation={navLocation}
                urlPath={urlPath}
              />
            )}
          </div>
        )}

        {!searchFocus && storePath === "market-place" && (
          <div
            className={`${styles.marketPlaceHeader} ${
              hasSearch ? styles.searchingActive : ""
            }`}
          >
            {isSwitching ? (
              <StoreIdentitySkeleton />
            ) : (
              <MarketplaceLogo onClick={handleLogoClick} />
            )}
          </div>
        )}

        {(showSearchBar || isSticky || searchFocus || hasTyped) &&
          (!isHomePage || searchFocus || hasTyped || pastHomeSearch) && (
            <div
              className={styles.searchContainer}
              style={{
                width: "auto",
                ...(searchFocus || searchTerm.trim() !== ""
                  ? { display: "block" }
                  : {}),
              }}
            >
              <HeaderSearchForm
                searchTerm={searchTerm}
                searchInputRef={searchInputRef}
                handleSearch={handleSearch}
                hasTyped={hasTyped}
                cancelSearch={cancelSearch}
                searchFocus={searchFocus}
                setSearchFocus={setSearchFocus}
                urlWithSearchQuery={urlWithSearchQuery}
                navLocation={navLocation}
                storePath={storePath}
                onSeeAllSearchResults={onSeeAllSearchResults}
              />
              <MarketplaceSearchPrompt
                searchTerm={searchTerm}
                storePath={storePath}
                navLocation={navLocation}
                onSeeAllSearchResults={onSeeAllSearchResults}
              />
            </div>
          )}

        {!searchFocus && (
          <div
            className={`${styles.locationCartBox} ${
              hasSearch ? styles.searchingActive : ""
            }`}
          >
            <HeaderActions
              modal={modal}
              showSearchBar={showSearchBar}
              accessPoints={accessPoints}
              accessPointId={accessPointId}
              storeBoxLocationChange={storeBoxLocationChange}
              storePath={storePath}
              isSticky={isSticky}
              setSearchFocus={setSearchFocus}
              setHasTyped={setHasTyped}
              isPageMode={isPageMode}
              showCart={showCart}
              cartCount={cartCount}
              onLogout={onLogout}
              handleLoginClick={handleLoginClick}
              organisation={organisation}
            />
          </div>
        )}
      </div>
    </header>
  );
};

export default StoreHeader;
